import { createElement, lazy, type ComponentType } from "react";
import type { FuseRouteItemType } from "@fuse/utils/FuseUtils";
import PermissaoRotaGuard from "@/permissoes/PermissaoRotaGuard";
import { classificarFalhaDeImportacao } from "@/utils/storage/staleChunkReload";

// ------------------------------- CONSTANTS -------------------------------
const CARREGADORES = new Map<string, () => Promise<void>>();

// --------------------------------- TYPES ---------------------------------
type DefinicaoRotaProtegida = {
	readonly path: string;
	readonly url: string;
	readonly tag: string;
};

type ModuloPagina = { default: ComponentType };
type NavegadorComConexao = Navigator & {
	readonly connection?: { readonly saveData?: boolean; readonly effectiveType?: string };
};

// -------------------------------- HELPERS --------------------------------
function permiteAntecipacao(): boolean {
	const conexao = (globalThis.navigator as NavegadorComConexao | undefined)?.connection;
	return !conexao?.saveData && conexao?.effectiveType !== "2g" && conexao?.effectiveType !== "slow-2g";
}

// ------------------------------- FUNCTIONS -------------------------------
export function criarRotaProtegida(
	definicao: Readonly<DefinicaoRotaProtegida>,
	carregar: () => Promise<ModuloPagina>,
): FuseRouteItemType {
	let promessa: Promise<ModuloPagina> | undefined;
	let usadaNaNavegacao = false;

	function carregarCompartilhado(): Promise<ModuloPagina> {
		if (promessa !== undefined) return promessa;
		promessa = Promise.resolve()
			.then(carregar)
			.catch((error: unknown) => {
				classificarFalhaDeImportacao(error, usadaNaNavegacao ? "navegacao" : "prefetch");
				promessa = undefined;
				usadaNaNavegacao = false;
				throw error;
			});
		return promessa;
	}

	CARREGADORES.set(definicao.url, async () => {
		try {
			await carregarCompartilhado();
		} catch {
			// Antecipação é especulativa; o clique pode tentar carregar novamente.
		}
	});

	const component = lazy(() => {
		usadaNaNavegacao = true;
		return carregarCompartilhado();
	});

	return {
		path: definicao.path,
		element: createElement(PermissaoRotaGuard, { route: definicao, component }),
	};
}

export async function anteciparRota(url: string): Promise<void> {
	if (!permiteAntecipacao()) return;
	await CARREGADORES.get(url)?.();
}
